import { Stack, TooltipProps, Typography } from '@mui/material';
import { ChevronDownSmallIcon, TagSelect } from '@schooly/style';
import { forwardRef, ReactNode, useCallback, useImperativeHandle, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CheckOverflow } from '../CheckOverflow';
import { FiltersDropdownButton } from './FiltersDropdownButton';
import { FiltersDropdownContainer } from './FiltersDropdownContainer';

type FilterDropdownProps = {
  label: string;
  selectLabel?: string;
  onClear?: () => void;
  tags: (onOpen: () => void) => ReactNode[];
  children?: (onClose: () => void) => ReactNode;
  disabled?: boolean;
  width?: number;
  hiddenTagsError?: boolean;
  testId?: string;
  showLabel?: boolean;
} & Partial<Omit<TooltipProps, 'children'>>;

export type FilterDropdownComponentRef = {
  hiddenTags: Set<number>;
};

export const FilterDropdown = forwardRef<FilterDropdownComponentRef, FilterDropdownProps>(
  (
    {
      tags,
      label,
      children,
      onClear,
      selectLabel,
      disabled,
      width = 400,
      hiddenTagsError,
      testId,
      showLabel = true,
      ...tooltipProps
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      hiddenTags,
    }));

    const [hiddenTags, setHiddenTags] = useState<Set<number>>(new Set());
    const [opened, setOpened] = useState(false);

    const open = useCallback(() => {
      setOpened(true);
    }, []);

    const close = useCallback(() => {
      setOpened(false);
    }, []);

    const setTagHidden = useCallback((index: number, isHidden: boolean) => {
      setHiddenTags((tags) => {
        if (isHidden) {
          tags.add(index);
        } else {
          tags.delete(index);
        }
        return new Set(tags);
      });
    }, []);

    const tagNodes = tags(open).map((tagNode, i) =>
      i === 0 ? (
        tagNode
      ) : (
        <CheckOverflow index={i} onHideTag={setTagHidden} key={`_${i}`}>
          {tagNode}
        </CheckOverflow>
      ),
    );

    return (
      <FiltersDropdownContainer
        opened={opened}
        onOpen={open}
        onClose={close}
        disabled={disabled}
        width={width}
        title={children ? children(close) : undefined}
        testId={testId}
        {...tooltipProps}
        renderClosed={() => (
          <Stack flexDirection="row" alignItems="center">
            {showLabel && (
              <Typography variant="h3" color="common.grey3" pr={1}>
                {label}
              </Typography>
            )}
            {tagNodes.length ? (
              tagNodes
            ) : (
              <Typography variant="h3">
                {selectLabel ?? <FormattedMessage id="filter-All" />}
              </Typography>
            )}{' '}
            {!!hiddenTags.size && (
              <TagSelect
                onClick={!disabled ? open : undefined}
                sx={(theme) => ({
                  backgroundColor: 'white',
                  ml: 0.5,
                  borderColor: hiddenTagsError
                    ? `${theme.palette.error.main} !important`
                    : undefined,
                  color: hiddenTagsError ? `${theme.palette.error.main} !important` : undefined,
                })}
                label={`+${hiddenTags.size}`}
              />
            )}
            <ChevronDownSmallIcon color="black" />
          </Stack>
        )}
        renderOpened={() => (
          <FiltersDropdownButton
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            onClear={
              onClear
                ? () => {
                    onClear();
                    close();
                  }
                : undefined
            }
          >
            {label}
          </FiltersDropdownButton>
        )}
      />
    );
  },
);
